<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { VueFlow, Handle, Position, MarkerType } from '@vue-flow/core';
import { Background } from '@vue-flow/background';
import ScenarioZoomControls from './ScenarioZoomControls.vue';
import '@vue-flow/core/dist/style.css';
import '@vue-flow/core/dist/theme-default.css';
import { useAlert } from 'dashboard/composables';
import ScenariosAPI from 'dashboard/api/scenarios';
import TemplatesAPI from 'dashboard/api/messageTemplates';
import BitrixStagesAPI from 'dashboard/api/bitrixStages';
import MedupFeedAPI from 'dashboard/api/medupFeed';

const stages = ref([]); // deal stages for human-readable pickers
const departments = ref([]); // branches (филиалы) the deals carry
const STAGE_FIELDS = ['stage', 'to_stage', 'from_stage'];
const isStageField = field => STAGE_FIELDS.includes(field);
const isBranchField = field => field === 'department_id';
const branchName = id =>
  (departments.value.find(d => String(d.id) === String(id)) || {}).name || id;
const CHANNELS = ['telegram', 'sms', 'email'];

const route = useRoute();
const router = useRouter();
const scenarioId = route.params.id;

const nodes = ref([]);
const edges = ref([]);
const scenario = ref({ name: '', status: 'draft' });
const templates = ref([]);
const selectedId = ref(null);
const saving = ref(false);
let flowInstance = null;

const EDGE = { type: 'smoothstep', markerEnd: MarkerType.ArrowClosed, animated: true };
const newId = () => `n${Math.random().toString(36).slice(2, 8)}`;

const NODE_LABEL = {
  trigger: 'Триггер',
  condition: 'Условие',
  wait: 'Ожидание',
  action: 'Действие',
  goal: 'Цель',
  exit: 'Выход',
};
// Config-panel header meta per node type: icon chip color (matches the canvas node) + one-line hint.
const PANEL_META = {
  trigger: { icon: 'i-lucide-zap', bg: '#e7f6ee', fg: '#1f9d57', hint: 'старт сценария' },
  condition: { icon: 'i-lucide-git-branch', bg: '#fbf1de', fg: '#b57e12', hint: 'ветвление: да / нет' },
  wait: { icon: 'i-lucide-clock', bg: '#e0f4f5', fg: '#0e9ba8', hint: 'пауза в потоке' },
  action: { icon: 'i-lucide-send', bg: '#eceeff', fg: '#5b6cff', hint: 'отправка сообщения' },
  goal: { icon: 'i-lucide-target', bg: '#e7f6ee', fg: '#1f9d57', hint: 'успешный финал' },
  exit: { icon: 'i-lucide-square', bg: '#eceff1', fg: '#78848b', hint: 'конец ветки' },
};

// ---------- load ----------
const edgeOf = (source, target, sourceHandle = null) => ({
  id: `e-${source}-${sourceHandle || 'o'}-${target}`,
  source,
  target,
  sourceHandle,
  ...EDGE,
});

const buildFromGraph = (graph, trigger) => {
  const g = graph || {};
  const gn = g.nodes || {};
  const ns = [];
  const es = [];

  const tpos = g.__trigger || { x: 60, y: 60 };
  ns.push({
    id: '__trigger__',
    type: 'trigger',
    position: { x: tpos.x, y: tpos.y },
    data: {
      entity: trigger?.type === 'schedule' ? 'contact' : 'deal',
      event: trigger?.type === 'schedule' ? 'Birthday' : trigger?.operation || 'DealCreated',
      schedule: trigger?.schedule || { mode: 'date_field', field: 'medup_dob', offset_days: 0 },
      filters: trigger?.filters || { match: 'all', rules: [] },
      frequency: trigger?.frequency || { mode: 'every' },
    },
  });
  if (g.start) es.push(edgeOf('__trigger__', g.start));

  let i = 0;
  Object.entries(gn).forEach(([id, n]) => {
    ns.push({
      id,
      type: n.type,
      position: { x: n.x ?? 340, y: n.y ?? 60 + i * 130 },
      data: { ...n },
    });
    i += 1;
    if (n.type === 'condition') {
      if (n.next_true) es.push(edgeOf(id, n.next_true, 'true'));
      if (n.next_false) es.push(edgeOf(id, n.next_false, 'false'));
    } else if (n.type !== 'exit' && n.next) {
      es.push(edgeOf(id, n.next));
    }
  });
  nodes.value = ns;
  edges.value = es;
};

const load = async () => {
  try {
    const [{ data: scn }, { data: tpls }, { data: stg }, { data: deps }] = await Promise.all([
      ScenariosAPI.show(scenarioId),
      TemplatesAPI.get(),
      BitrixStagesAPI.get(),
      MedupFeedAPI.departments().catch(() => ({ data: [] })),
    ]);
    scenario.value = scn;
    templates.value = tpls || [];
    stages.value = (stg || []).filter(s => s.stage_type === 'deal');
    departments.value = deps || [];
    buildFromGraph(scn.graph, scn.trigger);
    // fit the view AFTER nodes are loaded (pane-ready can fire on an empty canvas)
    setTimeout(() => flowInstance && flowInstance.fitView({ padding: 0.3 }), 150);
  } catch (e) {
    useAlert('Не удалось загрузить сценарий');
  }
};
onMounted(load);

// ---------- canvas interactions ----------
const onPaneReady = instance => {
  flowInstance = instance;
  setTimeout(() => instance.fitView(), 50);
};

const onConnect = params => {
  // one outgoing edge per (source, handle)
  edges.value = edges.value.filter(
    e =>
      !(e.source === params.source && (e.sourceHandle || null) === (params.sourceHandle || null))
  );
  edges.value.push({ ...EDGE, ...params, id: `e-${newId()}` });
};

const onNodeClick = ({ node }) => {
  selectedId.value = node.id;
};

const defaultData = type => {
  if (type === 'wait')
    return { mode: 'dynamic', field: 'appointment_at', amount: 24, unit: 'hours', direction: 'before' };
  if (type === 'action')
    return { channels: ['telegram'], template_id: null, ttl_hours: 0 };
  if (type === 'condition') return { definition: { match: 'all', rules: [] } };
  return {};
};

const toggleChannel = (node, ch) => {
  if (!Array.isArray(node.data.channels)) node.data.channels = [];
  const i = node.data.channels.indexOf(ch);
  if (i >= 0) node.data.channels.splice(i, 1);
  else node.data.channels.push(ch);
};
const stageName = statusId =>
  (stages.value.find(s => s.status_id === statusId) || {}).name || statusId;

const addNode = type => {
  const id = newId();
  // place new blocks left-to-right (horizontal flow, like the reference)
  const maxX = nodes.value.reduce((m, n) => Math.max(m, n.position.x), 0);
  nodes.value.push({
    id,
    type,
    position: { x: maxX + 260, y: 180 },
    data: defaultData(type),
  });
  selectedId.value = id;
};

const deleteSelected = () => {
  if (!selectedId.value || selectedId.value === '__trigger__') return;
  const id = selectedId.value;
  nodes.value = nodes.value.filter(n => n.id !== id);
  edges.value = edges.value.filter(e => e.source !== id && e.target !== id);
  selectedId.value = null;
};

const selectedNode = computed(() =>
  selectedId.value ? nodes.value.find(n => n.id === selectedId.value) : null
);

// Visual condition builder over the triggering event's fields.
const EVENT_FIELDS = [
  { value: 'department_id', label: 'Филиал' },
  { value: 'stage', label: 'Стадия' },
  { value: 'to_stage', label: 'Новая стадия' },
  { value: 'from_stage', label: 'Прежняя стадия' },
  { value: 'patient_type', label: 'Тип пациента' },
  { value: 'doctor', label: 'Врач' },
  { value: 'appointment_at', label: 'Дата приёма' },
  // computed time fields (resolved by RuleEvaluator regardless of source) — for scheduling guards
  { value: 'minutes_until_appointment', label: 'Минут до приёма' },
  { value: 'hours_until_appointment', label: 'Часов до приёма' },
  { value: 'hours_created_before_appointment', label: 'Часов от создания до приёма' },
  { value: 'hours_since_created', label: 'Часов с создания' },
];
const OPS = [
  { value: 'eq', label: '=' },
  { value: 'neq', label: '≠' },
  { value: 'contains', label: 'содержит' },
  { value: 'gt', label: '>' },
  { value: 'lt', label: '<' },
  { value: 'gte', label: '≥' },
  { value: 'lte', label: '≤' },
  { value: 'present', label: 'заполнено' },
  { value: 'blank', label: 'пусто' },
];

const ensureDefinition = node => {
  if (!node.data.definition) node.data.definition = { match: 'all', rules: [] };
  if (!Array.isArray(node.data.definition.rules)) node.data.definition.rules = [];
  return node.data.definition;
};
const addRule = node => {
  ensureDefinition(node).rules.push({ source: 'event', field: 'branch_id', op: 'eq', value: '' });
};
const removeRule = (node, idx) => {
  ensureDefinition(node).rules.splice(idx, 1);
};

// --- start-node entry filters + frequency cap ---
const ensureFilters = node => {
  if (!node.data.filters) node.data.filters = { match: 'all', rules: [] };
  if (!Array.isArray(node.data.filters.rules)) node.data.filters.rules = [];
  return node.data.filters;
};
const addFilterRule = node => {
  ensureFilters(node).rules.push({ source: 'event', field: 'branch_id', op: 'eq', value: '' });
};
const removeFilterRule = (node, idx) => {
  ensureFilters(node).rules.splice(idx, 1);
};
const ensureFrequency = node => {
  if (!node.data.frequency) node.data.frequency = { mode: 'every' };
  return node.data.frequency;
};

const conditionSummary = data => {
  const rules = data.definition?.rules || [];
  if (!rules.length) return 'без правил';
  const r = rules[0];
  const f = EVENT_FIELDS.find(x => x.value === r.field)?.label || r.field;
  const o = OPS.find(x => x.value === r.op)?.label || r.op;
  const tail = rules.length > 1 ? ` +${rules.length - 1}` : '';
  const val = isStageField(r.field)
    ? stageName(r.value)
    : isBranchField(r.field)
      ? branchName(r.value)
      : r.value ?? '';
  return `${f} ${o} ${val}${tail}`;
};
const UNIT_LABEL = { minutes: 'мин', hours: 'ч', days: 'сут' };
const waitSummary = data => {
  if (data.mode === 'window')
    return `окно ${data.window_from || '09:00'}–${data.window_to || '21:00'}`;
  const amt = data.amount != null ? data.amount : Math.abs(data.offset_minutes || 0);
  const unit = UNIT_LABEL[data.unit] || 'мин';
  if (data.mode === 'dynamic' || data.mode === 'relative_to_event') {
    const dir = data.direction === 'after' ? 'после' : 'до';
    return `${amt} ${unit} ${dir} · ${data.field || 'appointment_at'}`;
  }
  return `через ${amt} ${unit}`;
};

const CLIENT_FIELDS = [
  { value: 'name', label: 'Имя' },
  { value: 'phone', label: 'Телефон' },
  { value: 'email', label: 'Email' },
  { value: 'medup_sex_name', label: 'Пол' },
  { value: 'medup_dob', label: 'Дата рождения' },
  { value: 'cardnumber', label: 'Номер карты' },
];
const fieldsFor = source => (source === 'client' ? CLIENT_FIELDS : EVENT_FIELDS);

// Human description of each launch event: which deal change fires it + payload fields available.
const EVENT_INFO = {
  DealCreated: {
    label: 'Новая запись на приём',
    watches: 'появление сделки',
    desc: 'Срабатывает, когда в MRES создаётся новая сделка (приём).',
    fields: 'deal_id, branch_id, stage, appointment_at, doctor, patient_type',
  },
  DealStageChanged: {
    label: 'Смена стадии приёма',
    watches: 'поле «стадия» (from_stage → to_stage)',
    desc: 'Срабатывает при смене стадии сделки: записан → подтверждён → отменён и т.д.',
    fields: 'deal_id, from_stage, to_stage, branch_id, appointment_at, doctor',
  },
  DealRescheduled: {
    label: 'Перенос времени приёма',
    watches: 'поле «дата приёма» (appointment_at)',
    desc: 'Срабатывает, когда у сделки меняется дата/время приёма.',
    fields: 'deal_id, branch_id, appointment_at, doctor',
  },
};
// Entity → which events are available on it.
const ENTITIES = [
  { value: 'deal', label: 'Сделка (приём)' },
  { value: 'contact', label: 'Контакт (пациент)' },
];
const EVENTS_BY_ENTITY = {
  deal: [
    { value: 'DealCreated', label: 'Новая запись на приём' },
    { value: 'DealStageChanged', label: 'Смена стадии приёма' },
    { value: 'DealRescheduled', label: 'Перенос времени приёма' },
  ],
  contact: [{ value: 'Birthday', label: 'День рождения' }],
};
const eventsForEntity = entity => EVENTS_BY_ENTITY[entity] || [];
const eventLabel = (entity, event) =>
  (EVENTS_BY_ENTITY[entity] || []).find(e => e.value === event)?.label || event;
const onEntityChange = node => {
  const first = (EVENTS_BY_ENTITY[node.data.entity] || [])[0];
  node.data.event = first ? first.value : null;
};
const eventInfo = computed(() => EVENT_INFO[selectedNode.value?.data?.event] || null);

// ---------- save ----------
const serialize = () => {
  const gnodes = {};
  let trigger = { type: 'operation', operation: 'DealCreated' };
  let start = null;
  let triggerPos = { x: 60, y: 60 };

  nodes.value.forEach(node => {
    if (node.id === '__trigger__') {
      const filters = node.data.filters || { match: 'all', rules: [] };
      const frequency = node.data.frequency || { mode: 'every' };
      if (node.data.entity === 'contact') {
        trigger = {
          type: 'schedule',
          schedule: {
            mode: 'date_field',
            field: 'medup_dob',
            offset_days: node.data.schedule?.offset_days || 0,
          },
          filters,
          frequency,
        };
      } else {
        trigger = { type: 'operation', operation: node.data.event, filters, frequency };
      }
      triggerPos = { x: Math.round(node.position.x), y: Math.round(node.position.y) };
      return;
    }
    // copy ALL node settings; drop stale edge pointers (re-derived from edges below)
    const { next, next_true, next_false, ...rest } = node.data;
    gnodes[node.id] = {
      ...rest,
      type: node.type,
      x: Math.round(node.position.x),
      y: Math.round(node.position.y),
    };
  });

  edges.value.forEach(e => {
    if (e.source === '__trigger__') {
      start = e.target;
      return;
    }
    const n = gnodes[e.source];
    if (!n) return;
    if (n.type === 'condition') {
      if (e.sourceHandle === 'false') n.next_false = e.target;
      else n.next_true = e.target;
    } else {
      n.next = e.target;
    }
  });

  return {
    trigger,
    graph: { start, __trigger: triggerPos, nodes: gnodes },
  };
};

const save = async () => {
  saving.value = true;
  try {
    const { trigger, graph } = serialize();
    await ScenariosAPI.update(scenarioId, {
      name: scenario.value.name,
      trigger,
      graph,
    });
    useAlert('Сценарий сохранён');
  } catch (e) {
    useAlert('Не удалось сохранить сценарий');
    throw e;
  } finally {
    saving.value = false;
  }
};

const toggleActive = async () => {
  try {
    if (scenario.value.status === 'active') {
      await ScenariosAPI.pause(scenarioId);
      scenario.value.status = 'paused';
    } else {
      await save();
      await ScenariosAPI.activate(scenarioId);
      scenario.value.status = 'active';
    }
  } catch (e) {
    useAlert('Не удалось изменить статус');
  }
};

const publish = async () => {
  try {
    await save();
    await ScenariosAPI.activate(scenarioId);
    scenario.value.status = 'active';
    useAlert('Сценарий опубликован');
  } catch (e) {
    useAlert('Сохранено, но не удалось активировать');
  }
};
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-surface-1" style="font-family: 'Golos Text', system-ui, sans-serif">
    <div class="flex items-center gap-3 px-4 py-2.5 border-b border-n-weak bg-white">
      <button
        class="text-sm text-n-slate-11 hover:text-n-slate-12"
        @click="router.push({ name: 'automation_scenarios_index' })"
      >
        ‹ Сценарии
      </button>
      <input
        v-model="scenario.name"
        class="px-1 text-sm font-semibold border-0 rounded w-72 text-n-slate-12 focus:bg-n-alpha-1"
        placeholder="Имя сценария"
      />
      <span
        class="px-2.5 py-0.5 text-xs rounded-full flex items-center gap-1"
        :class="scenario.status === 'active' ? 'bg-n-teal-3 text-n-teal-11' : 'bg-n-alpha-2 text-n-slate-11'"
      >
        <span class="w-1.5 h-1.5 rounded-full" :style="{ background: scenario.status === 'active' ? '#1f9d57' : '#9aa5ab' }"></span>
        {{ scenario.status === 'active' ? 'Активен' : scenario.status === 'paused' ? 'Пауза' : 'Черновик' }}
      </span>

      <span class="flex-1"></span>
      <span class="text-xs text-n-slate-10">{{ saving ? 'Сохранение…' : '✓ Автосохранение' }}</span>
      <button
        class="px-3 py-1.5 text-sm border rounded-lg border-n-weak text-n-slate-11"
        @click="toggleActive"
      >
        ▶ {{ scenario.status === 'active' ? 'Пауза' : 'Активировать' }}
      </button>
      <button
        class="px-4 py-1.5 text-sm font-medium text-white rounded-lg disabled:opacity-40"
        style="background: #0e9ba8"
        :disabled="saving"
        @click="publish"
      >
        Опубликовать
      </button>
    </div>

    <div class="flex flex-1 min-h-0">
      <div class="relative flex-1 min-w-0">
        <VueFlow
          v-model:nodes="nodes"
          v-model:edges="edges"
          :min-zoom="0.2"
          :max-zoom="2"
          @connect="onConnect"
          @node-click="onNodeClick"
          @pane-ready="onPaneReady"
        >
          <Background :gap="16" pattern-color="#cbd5e1" />
          <ScenarioZoomControls />

          <template #node-trigger="{ data }">
            <div class="flow-node flow-trigger">
              <div class="flow-ic">
                <span :class="data.entity === 'contact' ? 'i-lucide-calendar-clock' : 'i-lucide-zap'" />
              </div>
              <div class="flow-body">
                <div class="flow-cat">Триггер · {{ data.entity === 'contact' ? 'расписание' : 'событие' }}</div>
                <div class="flow-title">{{ eventLabel(data.entity, data.event) }}</div>
                <div class="flow-sub">{{ data.entity === 'contact' ? 'по дате клиента' : 'сделка' }}</div>
              </div>
              <Handle id="next" type="source" :position="Position.Right" />
            </div>
          </template>

          <template #node-condition="{ data }">
            <div class="flow-node flow-condition">
              <Handle type="target" :position="Position.Left" />
              <div class="flow-ic"><span class="i-lucide-git-branch" /></div>
              <div class="flow-body">
                <div class="flow-cat">Условие · фильтр</div>
                <div class="flow-title">{{ conditionSummary(data) }}</div>
                <div class="flow-sub">
                  {{ (data.definition?.rules || []).length }} условия ·
                  {{ data.definition?.match === 'any' ? 'ИЛИ' : 'И' }}
                </div>
              </div>
              <Handle id="true" type="source" :position="Position.Right" :style="{ top: '38%' }" />
              <Handle id="false" type="source" :position="Position.Right" :style="{ top: '74%' }" />
              <span class="flow-pin flow-pin-yes">да</span>
              <span class="flow-pin flow-pin-no">нет</span>
            </div>
          </template>

          <template #node-wait="{ data }">
            <div class="flow-node flow-wait">
              <Handle type="target" :position="Position.Left" />
              <div class="flow-ic"><span class="i-lucide-clock" /></div>
              <div class="flow-body">
                <div class="flow-cat">Задержка</div>
                <div class="flow-title">{{ waitSummary(data) }}</div>
                <div class="flow-sub">{{ data.mode === 'window' ? 'окно отправки' : 'пауза' }}</div>
              </div>
              <Handle id="next" type="source" :position="Position.Right" />
            </div>
          </template>

          <template #node-action="{ data }">
            <div class="flow-node flow-action">
              <Handle type="target" :position="Position.Left" />
              <div class="flow-ic"><span class="i-lucide-send" /></div>
              <div class="flow-body">
                <div class="flow-cat">Канал · отправка</div>
                <div class="flow-title">
                  {{ (templates.find(t => t.id === data.template_id) || {}).name || 'нет шаблона' }}
                </div>
                <div class="flow-sub">
                  {{ (data.channels || [data.channel]).filter(Boolean).join(' → ') || 'канал не выбран' }}
                </div>
              </div>
              <Handle id="next" type="source" :position="Position.Right" />
            </div>
          </template>

          <template #node-goal>
            <div class="flow-node flow-goal">
              <Handle type="target" :position="Position.Left" />
              <div class="flow-ic"><span class="i-lucide-target" /></div>
              <div class="flow-body">
                <div class="flow-cat">Цель</div>
                <div class="flow-title">Цель достигнута</div>
                <div class="flow-sub">дошёл до результата</div>
              </div>
            </div>
          </template>

          <template #node-exit>
            <div class="flow-node flow-exit">
              <Handle type="target" :position="Position.Left" />
              <div class="flow-ic"><span class="i-lucide-square" /></div>
              <div class="flow-body">
                <div class="flow-cat">Выход</div>
                <div class="flow-title">Завершение</div>
                <div class="flow-sub">конец ветки</div>
              </div>
            </div>
          </template>
        </VueFlow>

        <div
          class="absolute z-10 flex items-center gap-1 px-2 py-1.5 -translate-x-1/2 bg-white border shadow-lg bottom-5 left-1/2 rounded-xl border-n-weak"
        >
          <span class="px-2 text-xs font-medium tracking-wide uppercase text-n-slate-10">Добавить</span>
          <button class="flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg hover:bg-n-alpha-1 text-n-slate-12" @click="addNode('wait')"><span class="i-lucide-clock size-3.5 text-n-teal-11" aria-hidden="true" />Задержка</button>
          <button class="flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg hover:bg-n-alpha-1 text-n-slate-12" @click="addNode('condition')"><span class="i-lucide-git-branch size-3.5 text-n-amber-11" aria-hidden="true" />Условие</button>
          <button class="flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg hover:bg-n-alpha-1 text-n-slate-12" @click="addNode('action')"><span class="i-lucide-send size-3.5 text-n-blue-11" aria-hidden="true" />Канал</button>
          <button class="flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg hover:bg-n-alpha-1 text-n-slate-12" @click="addNode('goal')"><span class="i-lucide-target size-3.5 text-n-teal-11" aria-hidden="true" />Цель</button>
          <button class="flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg hover:bg-n-alpha-1 text-n-slate-12" @click="addNode('exit')"><span class="i-lucide-square size-3.5 text-n-slate-10" aria-hidden="true" />Выход</button>
        </div>
      </div>

      <div
        v-if="selectedNode"
        class="p-4 overflow-auto border-l scn-panel w-96 border-n-weak bg-n-solid-1"
      >
        <div class="flex items-start justify-between gap-2 pb-3 mb-4 border-b border-n-weak">
          <div class="flex items-center gap-2.5 min-w-0">
            <div
              class="flex items-center justify-center rounded-lg shrink-0 size-8"
              :style="{ background: PANEL_META[selectedNode.type]?.bg, color: PANEL_META[selectedNode.type]?.fg }"
            >
              <span :class="PANEL_META[selectedNode.type]?.icon" class="size-4" aria-hidden="true" />
            </div>
            <div class="min-w-0">
              <div class="text-sm font-medium truncate text-n-slate-12">{{ NODE_LABEL[selectedNode.type] }}</div>
              <div class="text-xs truncate text-n-slate-10">{{ PANEL_META[selectedNode.type]?.hint }}</div>
            </div>
          </div>
          <button
            v-if="selectedNode.id !== '__trigger__'"
            class="p-1.5 rounded-md shrink-0 text-n-slate-10 hover:text-n-ruby-11 hover:bg-n-alpha-1"
            title="Удалить ноду"
            @click="deleteSelected"
          >
            <span class="i-lucide-trash-2 size-4" aria-hidden="true" />
          </button>
        </div>

        <template v-if="selectedNode.id === '__trigger__'">
          <label class="block mb-2 text-xs text-n-slate-11">1. Сущность
            <select
              v-model="selectedNode.data.entity"
              class="w-full h-9 px-3 mt-1 text-sm border rounded-md border-n-weak bg-n-surface-1"
              @change="onEntityChange(selectedNode)"
            >
              <option v-for="e in ENTITIES" :key="e.value" :value="e.value">{{ e.label }}</option>
            </select>
          </label>

          <label class="block mb-2 text-xs text-n-slate-11">2. Событие
            <select v-model="selectedNode.data.event" class="w-full h-9 px-3 mt-1 text-sm border rounded-md border-n-weak bg-n-surface-1">
              <option v-for="ev in eventsForEntity(selectedNode.data.entity)" :key="ev.value" :value="ev.value">
                {{ ev.label }}
              </option>
            </select>
          </label>

          <div v-if="eventInfo" class="p-3 mb-4 text-xs leading-relaxed rounded-lg bg-n-alpha-1 text-n-slate-11">
            <div>{{ eventInfo.desc }}</div>
            <div class="mt-1">Отслеживается: <b>{{ eventInfo.watches }}</b></div>
            <div class="mt-1">Поля события: <code class="text-n-slate-12">{{ eventInfo.fields }}</code></div>
          </div>

          <template v-if="selectedNode.data.entity === 'contact' && selectedNode.data.event === 'Birthday'">
            <label class="block mb-2 text-xs text-n-slate-11">За сколько дней до даты (0 = в сам день)
              <input v-model.number="selectedNode.data.schedule.offset_days" type="number" min="0" class="w-full h-9 px-3 mt-1 text-sm border rounded-md border-n-weak bg-n-surface-1" />
            </label>
            <div class="p-3 mb-4 text-xs leading-relaxed rounded-lg bg-n-alpha-1 text-n-slate-11">
              Ежедневно в 09:00 находит клиентов, у кого ДР в этот день (или за N дней до), и заводит в сценарий. Один раз в год.
            </div>
          </template>

          <div class="pt-3 mb-2 border-t border-n-weak">
            <div class="mb-1 text-xs font-medium text-n-slate-12">3. Фильтр на входе</div>
            <select
              :value="ensureFilters(selectedNode).match"
              class="w-full h-8 px-2.5 mb-2 text-xs border rounded-md border-n-weak bg-n-surface-1"
              @change="ensureFilters(selectedNode).match = $event.target.value"
            >
              <option value="all">все правила (И)</option>
              <option value="any">любое правило (ИЛИ)</option>
            </select>
            <div
              v-for="(rule, idx) in ensureFilters(selectedNode).rules"
              :key="idx"
              class="relative p-3 mb-2 space-y-2 border rounded-lg border-n-weak"
            >
              <button
                class="absolute flex items-center justify-center w-5 h-5 text-sm leading-none rounded top-2 right-2 text-n-slate-10 hover:text-n-ruby-11 hover:bg-n-alpha-2"
                title="Удалить правило"
                @click="removeFilterRule(selectedNode, idx)"
              >
                ×
              </button>
              <div class="mr-5">
                <div class="mb-1 text-[11px] text-n-slate-10">Источник</div>
                <select v-model="rule.source" class="w-full h-8 px-2.5 text-xs border rounded-md border-n-weak bg-n-surface-1">
                  <option value="event">из события</option>
                  <option value="deal">текущая сделка</option>
                  <option value="client">клиент (профиль)</option>
                </select>
              </div>
              <div>
                <div class="mb-1 text-[11px] text-n-slate-10">Поле</div>
                <select v-model="rule.field" class="w-full h-8 px-2.5 text-xs border rounded-md border-n-weak bg-n-surface-1">
                  <option v-for="f in fieldsFor(rule.source)" :key="f.value" :value="f.value">{{ f.label }}</option>
                </select>
              </div>
              <div>
                <div class="mb-1 text-[11px] text-n-slate-10">Условие</div>
                <div class="flex gap-2">
                  <select v-model="rule.op" class="flex-none h-8 px-2 text-xs border rounded-md w-28 border-n-weak bg-n-surface-1">
                    <option v-for="o in OPS" :key="o.value" :value="o.value">{{ o.label }}</option>
                  </select>
                  <select
                    v-if="!['present', 'blank'].includes(rule.op) && isStageField(rule.field)"
                    v-model="rule.value"
                    class="flex-1 min-w-0 h-8 px-2.5 text-xs border rounded-md border-n-weak bg-n-surface-1"
                  >
                    <option value="">— стадия —</option>
                    <option v-for="s in stages" :key="s.status_id" :value="s.status_id">{{ s.name }}</option>
                  </select>
                  <select
                    v-else-if="!['present', 'blank'].includes(rule.op) && isBranchField(rule.field)"
                    v-model="rule.value"
                    class="flex-1 min-w-0 h-8 px-2.5 text-xs border rounded-md border-n-weak bg-n-surface-1"
                  >
                    <option value="">— филиал —</option>
                    <option v-for="d in departments" :key="d.id" :value="String(d.id)">{{ d.name }}</option>
                  </select>
                  <input
                    v-else-if="!['present', 'blank'].includes(rule.op)"
                    v-model="rule.value"
                    placeholder="значение"
                    class="flex-1 min-w-0 h-8 px-2.5 text-xs border rounded-md border-n-weak bg-n-surface-1"
                  />
                  <span v-else class="flex items-center flex-1 text-xs text-n-slate-9">значение не требуется</span>
                </div>
              </div>
            </div>
            <button class="w-full px-2 py-1 text-xs border rounded border-n-weak text-n-slate-11" @click="addFilterRule(selectedNode)">
              + Правило
            </button>
            <p class="mt-1 text-xs text-n-slate-10">В сценарий войдут только подходящие под фильтр.</p>
          </div>

          <div class="pt-3 border-t border-n-weak">
            <div class="mb-1 text-xs font-medium text-n-slate-12">Ограничение на клиента</div>
            <select v-model="ensureFrequency(selectedNode).mode" class="w-full h-9 px-3 mt-1 text-sm border rounded-md border-n-weak bg-n-surface-1">
              <option value="every">каждый раз</option>
              <option value="once">один раз (за всё время)</option>
              <option value="limited">не чаще раза за период</option>
            </select>
            <div v-if="selectedNode.data.frequency.mode === 'limited'" class="flex gap-2 mt-2">
              <input v-model.number="selectedNode.data.frequency.per_amount" type="number" min="1" placeholder="1" class="w-16 h-8 px-2.5 text-xs border rounded-md border-n-weak bg-n-surface-1" />
              <select v-model="selectedNode.data.frequency.per_unit" class="flex-1 h-8 px-2.5 text-xs border rounded-md border-n-weak bg-n-surface-1">
                <option value="hours">часов</option>
                <option value="days">суток</option>
                <option value="weeks">недель</option>
                <option value="months">месяцев</option>
              </select>
            </div>
            <p class="mt-1 text-xs text-n-slate-10">Анти-спам: не запускать одного пациента слишком часто.</p>
          </div>
        </template>

        <template v-else-if="selectedNode.type === 'wait'">
          <label class="block mb-2 text-xs text-n-slate-11">Режим
            <select v-model="selectedNode.data.mode" class="w-full h-9 px-3 mt-1 text-sm border rounded-md border-n-weak bg-n-surface-1">
              <option value="fixed">Фиксированное (от входа)</option>
              <option value="dynamic">Динамическое (относительно даты)</option>
              <option value="window">Окно отправки (часы)</option>
            </select>
          </label>

          <template v-if="selectedNode.data.mode === 'window'">
            <div class="flex gap-2">
              <label class="flex-1 block mb-2 text-xs text-n-slate-11">С
                <input v-model="selectedNode.data.window_from" type="time" class="w-full h-9 px-3 mt-1 text-sm border rounded-md border-n-weak bg-n-surface-1" />
              </label>
              <label class="flex-1 block mb-2 text-xs text-n-slate-11">До
                <input v-model="selectedNode.data.window_to" type="time" class="w-full h-9 px-3 mt-1 text-sm border rounded-md border-n-weak bg-n-surface-1" />
              </label>
            </div>
            <p class="text-xs text-n-slate-10">Прохождение продолжится только в этом окне (не ночью).</p>
          </template>

          <template v-else>
            <label v-if="selectedNode.data.mode === 'dynamic'" class="block mb-2 text-xs text-n-slate-11">Поле даты
              <select v-model="selectedNode.data.field" class="w-full h-9 px-3 mt-1 text-sm border rounded-md border-n-weak bg-n-surface-1">
                <option value="appointment_at">Дата приёма</option>
              </select>
            </label>
            <div class="flex gap-2 mb-1">
              <label class="block text-xs w-20 text-n-slate-11">Кол-во
                <input v-model.number="selectedNode.data.amount" type="number" min="0" class="w-full h-9 px-3 mt-1 text-sm border rounded-md border-n-weak bg-n-surface-1" />
              </label>
              <label class="flex-1 block text-xs text-n-slate-11">Единица
                <select v-model="selectedNode.data.unit" class="w-full h-9 px-3 mt-1 text-sm border rounded-md border-n-weak bg-n-surface-1">
                  <option value="minutes">минут</option>
                  <option value="hours">часов</option>
                  <option value="days">суток</option>
                </select>
              </label>
            </div>
            <label v-if="selectedNode.data.mode === 'dynamic'" class="block mb-2 text-xs text-n-slate-11">Когда
              <select v-model="selectedNode.data.direction" class="w-full h-9 px-3 mt-1 text-sm border rounded-md border-n-weak bg-n-surface-1">
                <option value="before">до даты</option>
                <option value="after">после даты</option>
              </select>
            </label>
          </template>
        </template>

        <template v-else-if="selectedNode.type === 'action'">
          <div class="mb-2 text-xs text-n-slate-11">
            Каскад каналов (по порядку, фолбэк если не доставлено)
            <div class="flex flex-col gap-1 mt-1">
              <label v-for="ch in CHANNELS" :key="ch" class="flex items-center gap-2">
                <input
                  type="checkbox"
                  :checked="(selectedNode.data.channels || []).includes(ch)"
                  @change="toggleChannel(selectedNode, ch)"
                />
                <span>{{ ch }}</span>
                <span v-if="(selectedNode.data.channels || []).includes(ch)" class="text-n-slate-10">
                  #{{ (selectedNode.data.channels || []).indexOf(ch) + 1 }}
                </span>
              </label>
            </div>
          </div>
          <label class="block mb-2 text-xs text-n-slate-11">Шаблон
            <select v-model="selectedNode.data.template_id" class="w-full h-9 px-3 mt-1 text-sm border rounded-md border-n-weak bg-n-surface-1">
              <option :value="null">— выбрать —</option>
              <option v-for="t in templates" :key="t.id" :value="t.id">{{ t.name }}</option>
            </select>
          </label>
          <label class="block mb-2 text-xs text-n-slate-11">Период актуальности, часов (0 = без ограничения)
            <input v-model.number="selectedNode.data.ttl_hours" type="number" min="0" class="w-full h-9 px-3 mt-1 text-sm border rounded-md border-n-weak bg-n-surface-1" />
            <span class="text-n-slate-10">если с момента события прошло больше — отправка пропускается</span>
          </label>
        </template>

        <template v-else-if="selectedNode.type === 'condition'">
          <label class="block mb-2 text-xs text-n-slate-11">Совпадение
            <select
              :value="(selectedNode.data.definition || {}).match || 'all'"
              class="w-full h-9 px-3 mt-1 text-sm border rounded-md border-n-weak bg-n-surface-1"
              @change="ensureDefinition(selectedNode).match = $event.target.value"
            >
              <option value="all">все правила (И)</option>
              <option value="any">любое правило (ИЛИ)</option>
            </select>
          </label>

          <div
            v-for="(rule, idx) in ensureDefinition(selectedNode).rules"
            :key="idx"
            class="relative p-3 mb-2 space-y-2 border rounded-lg border-n-weak"
          >
            <button
              class="absolute flex items-center justify-center w-5 h-5 text-sm leading-none rounded top-2 right-2 text-n-slate-10 hover:text-n-ruby-11 hover:bg-n-alpha-2"
              title="Удалить правило"
              @click="removeRule(selectedNode, idx)"
            >
              ×
            </button>
            <div class="mr-5">
              <div class="mb-1 text-[11px] text-n-slate-10">Источник</div>
              <select v-model="rule.source" class="w-full h-8 px-2.5 text-xs border rounded-md border-n-weak bg-n-surface-1">
                <option value="event">из события (момент триггера)</option>
                <option value="deal">текущая сделка (живое состояние)</option>
                <option value="client">клиент (профиль)</option>
              </select>
            </div>
            <div>
              <div class="mb-1 text-[11px] text-n-slate-10">Поле</div>
              <select v-model="rule.field" class="w-full h-8 px-2.5 text-xs border rounded-md border-n-weak bg-n-surface-1">
                <option v-for="f in fieldsFor(rule.source)" :key="f.value" :value="f.value">{{ f.label }}</option>
              </select>
            </div>
            <div>
              <div class="mb-1 text-[11px] text-n-slate-10">Условие</div>
              <div class="flex gap-2">
                <select v-model="rule.op" class="flex-none h-8 px-2 text-xs border rounded-md w-28 border-n-weak bg-n-surface-1">
                  <option v-for="o in OPS" :key="o.value" :value="o.value">{{ o.label }}</option>
                </select>
                <select
                  v-if="!['present', 'blank'].includes(rule.op) && isStageField(rule.field)"
                  v-model="rule.value"
                  class="flex-1 min-w-0 h-8 px-2.5 text-xs border rounded-md border-n-weak bg-n-surface-1"
                >
                  <option value="">— стадия —</option>
                  <option v-for="s in stages" :key="s.status_id" :value="s.status_id">{{ s.name }}</option>
                </select>
                <select
                  v-else-if="!['present', 'blank'].includes(rule.op) && isBranchField(rule.field)"
                  v-model="rule.value"
                  class="flex-1 min-w-0 h-8 px-2.5 text-xs border rounded-md border-n-weak bg-n-surface-1"
                >
                  <option value="">— филиал —</option>
                  <option v-for="d in departments" :key="d.id" :value="String(d.id)">{{ d.name }}</option>
                </select>
                <input
                  v-else-if="!['present', 'blank'].includes(rule.op)"
                  v-model="rule.value"
                  placeholder="значение"
                  class="flex-1 min-w-0 h-8 px-2.5 text-xs border rounded-md border-n-weak bg-n-surface-1"
                />
                <span v-else class="flex items-center flex-1 text-xs text-n-slate-9">значение не требуется</span>
              </div>
            </div>
          </div>

          <button
            class="w-full px-2 py-1 text-xs border rounded border-n-weak text-n-slate-11"
            @click="addRule(selectedNode)"
          >
            + Правило
          </button>
          <p class="mt-2 text-xs text-n-slate-10">
            Проверяются поля сделки из события. Выход «да» — подходит, «нет» — иначе.
          </p>
        </template>

        <template v-else>
          <p class="text-xs text-n-slate-10">Нет параметров.</p>
        </template>
      </div>
    </div>
  </div>
</template>

<style>
@import url('https://fonts.googleapis.com/css2?family=Golos+Text:wght@400;500;600&display=swap');

.vue-flow,
.flow-node {
  font-family: 'Golos Text', system-ui, sans-serif;
}
.flow-node {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 11px;
  width: 244px;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid #e6eaec;
  background: #fff;
  font-size: 12px;
  box-shadow: 0 1px 2px rgba(20, 28, 33, 0.05), 0 5px 14px rgba(20, 28, 33, 0.05);
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
}
.flow-node:hover {
  border-color: #d6dcde;
  box-shadow: 0 2px 4px rgba(20, 28, 33, 0.07), 0 10px 22px rgba(20, 28, 33, 0.09);
}
.vue-flow .selected .flow-node {
  border-color: #0e9ba8;
  box-shadow: 0 0 0 2px rgba(14, 155, 168, 0.35), 0 5px 14px rgba(20, 28, 33, 0.06);
}
.flow-ic {
  flex: none;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  display: grid;
  place-items: center;
}
.flow-ic > span { width: 18px; height: 18px; }
.flow-body { min-width: 0; }
/* Horizontal flow: drag from a right (source) dot to a left (target) dot. */
.vue-flow__handle {
  width: 11px;
  height: 11px;
  background: #fff;
  border: 2px solid #b8c0c4;
  box-shadow: none;
  transition: border-color 0.15s;
}
.vue-flow__handle:hover { border-color: #0e9ba8; background: #e6f6f7; cursor: crosshair; }
.vue-flow__handle-left { left: -6px; }
.vue-flow__handle-right { right: -6px; }
.vue-flow__edge-path { stroke: #c4ccd0; stroke-width: 1.6; }
.vue-flow .selected .vue-flow__edge-path { stroke: #0e9ba8; }
.flow-cat {
  font-size: 9.5px;
  font-weight: 500;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #8a959b;
  margin-bottom: 2px;
}
.flow-title { font-weight: 500; font-size: 13px; color: #1a2226; line-height: 1.3; }
.flow-sub { color: #97a1a7; margin-top: 2px; font-size: 11.5px; line-height: 1.3; }
.flow-trigger .flow-ic { background: #e7f6ee; color: #1f9d57; }
.flow-trigger .flow-cat { color: #1f9d57; }
.flow-condition .flow-ic { background: #fbf1de; color: #b57e12; }
.flow-condition .flow-cat { color: #b57e12; }
.flow-wait .flow-ic { background: #e0f4f5; color: #0e9ba8; }
.flow-wait .flow-cat { color: #0e9ba8; }
.flow-action .flow-ic { background: #eceeff; color: #5b6cff; }
.flow-action .flow-cat { color: #5b6cff; }
.flow-goal .flow-ic { background: #e7f6ee; color: #1f9d57; }
.flow-goal .flow-cat { color: #1f9d57; }
.flow-exit .flow-ic { background: #eceff1; color: #78848b; }
.flow-exit .flow-cat { color: #78848b; }
.flow-pin {
  position: absolute;
  right: -18px;
  font-size: 9px;
  font-weight: 500;
  padding: 1px 5px;
  border-radius: 6px;
  color: #6b7378;
  background: #eef1f2;
}
.flow-pin-yes { top: 30%; color: #1f7d52; background: #e7f6ee; }
.flow-pin-no { top: 66%; color: #a24b3d; background: #f9ecea; }

/* Config-panel form controls: native <select> arrow was clipping — replace with a clean chevron. */
.scn-panel select {
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%238a959b' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'><path d='m6 9 6 6 6-6'/></svg>");
  background-repeat: no-repeat;
  background-position: right 9px center;
  padding-right: 30px;
  cursor: pointer;
}
.scn-panel select,
.scn-panel input {
  transition: border-color 0.12s ease, box-shadow 0.12s ease;
}
.scn-panel select:focus,
.scn-panel input:focus {
  outline: none;
  border-color: #0e9ba8;
  box-shadow: 0 0 0 3px rgba(14, 155, 168, 0.14);
}
</style>
